import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import DOMPurify from 'dompurify';
import 'highlight.js/styles/github.css';
import { coursesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Custom styles for unified lesson experience
const unifiedLessonStyles = `
  .unified-lesson-content .markdown-section + .image-section,
  .unified-lesson-content .image-section + .markdown-section,
  .unified-lesson-content .audio-section + .markdown-section,
  .unified-lesson-content .markdown-section + .audio-section {
    margin-top: 2rem;
  }
  
  .unified-lesson-content .markdown-section + .audio-section {
    margin-top: 3rem;
  }
  
  .unified-lesson-content .audio-section + .image-section,
  .unified-lesson-content .image-section + .audio-section {
    margin-top: 2.5rem;
  }
  
  .unified-lesson-content .markdown-section:first-child h1:first-child {
    margin-top: 0;
  }
  
  .unified-lesson-content .pdf-section,
  .unified-lesson-content .video-section {
    margin: 3rem 0;
  }
  
  .unified-lesson-content .document-section {
    margin: 2.5rem 0;
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const LessonView: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const { user } = useAuth();
  const [course, setCourse] = useState<any>(null);
  const [currentLesson, setCurrentLesson] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fileUrls, setFileUrls] = useState<{ [key: number]: string }>({});
  const [progress, setProgress] = useState<any>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [fullscreenPdf, setFullscreenPdf] = useState<number | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<number | null>(null);
  const [pdfZoom, setPdfZoom] = useState(100);
  const [imageZoom, setImageZoom] = useState(100);
  const [showPdfControls, setShowPdfControls] = useState(true);
  const [showImageControls, setShowImageControls] = useState(true);
  const [markdownContent, setMarkdownContent] = useState<{ [key: number]: string }>({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!courseId || !lessonId) return;

      try {
        setLoading(true);
        const [courseData, progressData] = await Promise.all([
          coursesAPI.getCourse(parseInt(courseId)),
          coursesAPI.getCourseProgress(parseInt(courseId))
        ]);

        setCourse(courseData);
        setProgress(progressData);

        const lesson = courseData.lessons.find((l: any) => l.id === parseInt(lessonId));
        if (!lesson) {
          setError('Lesson not found');
          return;
        }

        setCurrentLesson(lesson);
        // isCompleted will be updated when progress data is available

        // Fetch signed URLs for files
        if (lesson.files && lesson.files.length > 0) {
          const urlPromises = lesson.files.map(async (file: any) => {
            try {
              const response = await coursesAPI.getFileSignedUrl(file.id);
              return { fileId: file.id, url: response.signed_url };
            } catch (error) {
              console.error(`Failed to get signed URL for file ${file.id}:`, error);
              return { fileId: file.id, url: null };
            }
          });

          const urls = await Promise.all(urlPromises);
          const urlMap = urls.reduce((acc, { fileId, url }) => {
            if (url) acc[fileId] = url;
            return acc;
          }, {} as { [key: number]: string });

          setFileUrls(urlMap);

          // Fetch markdown content for markdown files
          const markdownFiles = lesson.files.filter((file: any) => 
            file.file_type.toLowerCase() === 'md' || file.file_type.toLowerCase() === 'markdown'
          );
          
          if (markdownFiles.length > 0) {
            const markdownPromises = markdownFiles.map(async (file: any) => {
              const url = urlMap[file.id];
              if (url) {
                try {
                  const response = await fetch(url);
                  const content = await response.text();
                  return { fileId: file.id, content };
                } catch (error) {
                  console.error(`Failed to fetch markdown content for file ${file.id}:`, error);
                  return { fileId: file.id, content: null };
                }
              }
              return { fileId: file.id, content: null };
            });

            const markdownData = await Promise.all(markdownPromises);
            const markdownMap = markdownData.reduce((acc, { fileId, content }) => {
              if (content) acc[fileId] = content;
              return acc;
            }, {} as { [key: number]: string });

            setMarkdownContent(markdownMap);
          }
        }
      } catch (error: any) {
        setError(error.response?.data?.detail || 'Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, lessonId]);

  // Update completion status when progress data changes
  useEffect(() => {
    if (progress && currentLesson) {
      setIsCompleted(progress.completed_lesson_ids.includes(currentLesson.id));
    }
  }, [progress, currentLesson]);

  // Scroll to top when lesson changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lessonId]);

  // Reading progress tracker
  useEffect(() => {
    const handleScroll = () => {
      if (fullscreenPdf || fullscreenImage) return; // Don't track progress in fullscreen mode
      
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setReadingProgress(Math.min(100, Math.max(0, scrollPercent)));
      setShowProgress(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fullscreenPdf, fullscreenImage]);

  // Fullscreen controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (fullscreenPdf || fullscreenImage) {
        if (e.key === 'Escape') {
          setFullscreenPdf(null);
          setFullscreenImage(null);
        } else if (e.key === '+' || e.key === '=') {
          if (fullscreenPdf) {
            setPdfZoom(prev => Math.min(200, prev + 10));
          } else if (fullscreenImage) {
            setImageZoom(prev => Math.min(200, prev + 10));
          }
        } else if (e.key === '-') {
          if (fullscreenPdf) {
            setPdfZoom(prev => Math.max(50, prev - 10));
          } else if (fullscreenImage) {
            setImageZoom(prev => Math.max(25, prev - 10));
          }
        } else if (e.key === '0') {
          if (fullscreenPdf) {
            setPdfZoom(100);
          } else if (fullscreenImage) {
            setImageZoom(100);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [fullscreenPdf, fullscreenImage]);

  const handleCompleteLesson = async () => {
    if (!user || !currentLesson) return;
    
    try {
      await coursesAPI.completeLesson(currentLesson.id);
      setIsCompleted(true);
      
      // Refresh progress
      if (courseId) {
        const updatedProgress = await coursesAPI.getCourseProgress(parseInt(courseId));
        setProgress(updatedProgress);
      }
    } catch (error) {
      console.error('Failed to mark lesson as complete:', error);
    }
  };

  const downloadFile = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderImageViewer = (file: any, signedUrl: string) => {
    const isFullscreen = fullscreenImage === file.id;
    
    if (isFullscreen) {
      return (
        <div className="fixed inset-0 z-50 bg-black">
          {/* Image Toolbar */}
          <div className={`absolute top-0 left-0 right-0 z-60 bg-black/90 backdrop-blur-sm border-b border-gray-700 transition-all duration-300 ${
            showImageControls ? 'translate-y-0' : '-translate-y-full'
          }`}>
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setFullscreenImage(null)}
                  className="text-white hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-white/10"
                  title="Exit fullscreen (Esc)"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="text-white">
                  <h3 className="font-medium">{file.original_filename}</h3>
                  <p className="text-sm text-gray-300">Image Resource</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Zoom Controls */}
                <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
                  <button
                    onClick={() => setImageZoom(prev => Math.max(25, prev - 10))}
                    className="text-white hover:text-gray-300 transition-colors p-1"
                    title="Zoom out (-)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-white text-sm min-w-12 text-center">{imageZoom}%</span>
                  <button
                    onClick={() => setImageZoom(prev => Math.min(200, prev + 10))}
                    className="text-white hover:text-gray-300 transition-colors p-1"
                    title="Zoom in (+)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setImageZoom(100)}
                    className="text-white hover:text-gray-300 transition-colors p-1 text-xs"
                    title="Reset zoom (0)"
                  >
                    100%
                  </button>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => downloadFile(signedUrl, file.original_filename)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  title="Download Image"
                >
                  Download
                </button>
                
                {/* Controls Toggle */}
                <button
                  onClick={() => setShowImageControls(!showImageControls)}
                  className="text-white hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-white/10"
                  title="Toggle controls"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Image Content */}
          <div 
            className="w-full h-full flex items-center justify-center cursor-pointer overflow-auto p-4"
            onClick={() => setShowImageControls(!showImageControls)}
          >
            <img
              src={signedUrl}
              alt={file.original_filename}
              className="max-w-none max-h-none rounded-lg shadow-2xl"
              style={{ 
                transform: `scale(${imageZoom / 100})`,
                transformOrigin: 'center center'
              }}
            />
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-3 text-white text-xs">
            <div className="space-y-1">
              <div><kbd className="bg-white/20 px-1 rounded">Esc</kbd> Exit fullscreen</div>
              <div><kbd className="bg-white/20 px-1 rounded">+/-</kbd> Zoom in/out</div>
              <div><kbd className="bg-white/20 px-1 rounded">0</kbd> Reset zoom</div>
              <div><kbd className="bg-white/20 px-1 rounded">Click</kbd> Toggle controls</div>
            </div>
          </div>
        </div>
      );
    }

    // Regular image viewer - full view
    return (
      <div className="my-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative group">
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 z-10 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 flex space-x-4">
              <button
                onClick={() => {
                  setImageZoom(100);
                  setFullscreenImage(file.id);
                }}
                className="bg-black/70 hover:bg-black/80 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
              >
                🔍 View Fullscreen
              </button>
              <button
                onClick={() => downloadFile(signedUrl, file.original_filename)}
                className="bg-blue-600/80 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
              >
                ⬇️ Download
              </button>
            </div>
          </div>
          
          {/* Image Preview */}
                      <img
              src={signedUrl}
              alt={file.original_filename}
              className="w-full h-auto cursor-pointer"
              style={{ minHeight: '70vh', objectFit: 'contain' }}
              onClick={() => {
                setImageZoom(100);
                setFullscreenImage(file.id);
              }}
            />
        </div>
      </div>
    );
  };

  const renderMarkdownViewer = (file: any, signedUrl: string) => {
    const content = markdownContent[file.id];
    
    if (!content) {
      return (
        <div className="my-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-center py-32">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-600">Loading document...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="my-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative group">
          {/* Hover Overlay */}
          <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-all duration-300">
            <button
              onClick={() => downloadFile(signedUrl, file.original_filename)}
              className="bg-blue-600/90 hover:bg-blue-600 text-white px-4 py-2 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm text-sm"
            >
              ⬇️ Download
            </button>
          </div>
          
          {/* Markdown Content */}
          <div className="p-12 md:p-16 lg:p-20">
            <article className="prose prose-lg prose-gray max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  // Custom styling for different elements
                  h1: ({ node, children, ...props }) => <h1 className="text-4xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-200" {...props}>{children}</h1>,
                  h2: ({ node, children, ...props }) => <h2 className="text-3xl font-semibold text-gray-900 mb-6 mt-12" {...props}>{children}</h2>,
                  h3: ({ node, children, ...props }) => <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-8" {...props}>{children}</h3>,
                  h4: ({ node, children, ...props }) => <h4 className="text-xl font-semibold text-gray-900 mb-3 mt-6" {...props}>{children}</h4>,
                  h5: ({ node, children, ...props }) => <h5 className="text-lg font-semibold text-gray-900 mb-2 mt-4" {...props}>{children}</h5>,
                  h6: ({ node, children, ...props }) => <h6 className="text-base font-semibold text-gray-900 mb-2 mt-4" {...props}>{children}</h6>,
                  p: ({ node, ...props }) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
                  a: ({ node, children, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline" {...props}>{children}</a>,
                  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-6 space-y-2" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-6 space-y-2" {...props} />,
                  li: ({ node, ...props }) => <li className="text-gray-700 leading-relaxed" {...props} />,
                  blockquote: ({ node, ...props }) => (
                    <blockquote className="border-l-4 border-blue-500 pl-6 py-2 my-6 bg-blue-50 rounded-r-lg" {...props} />
                  ),
                  code: ({ node, ...props }: any) => 
                    props.inline ? (
                      <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono" {...props} />
                    ) : (
                      <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono" {...props} />
                    ),
                  pre: ({ node, ...props }) => (
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-6" {...props} />
                  ),
                  table: ({ node, ...props }) => (
                    <div className="overflow-x-auto mb-6">
                      <table className="min-w-full border border-gray-200 rounded-lg" {...props} />
                    </div>
                  ),
                  thead: ({ node, ...props }) => <thead className="bg-gray-50" {...props} />,
                  th: ({ node, ...props }) => <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b border-gray-200" {...props} />,
                  td: ({ node, ...props }) => <td className="px-4 py-3 text-gray-700 border-b border-gray-100" {...props} />,
                  hr: ({ node, ...props }) => <hr className="my-8 border-gray-200" {...props} />,
                  img: ({ node, alt, ...props }) => (
                    <img className="max-w-full h-auto rounded-lg shadow-lg mx-auto my-6" alt={alt || 'Image'} {...props} />
                  ),
                }}
              >
                {DOMPurify.sanitize(content)}
              </ReactMarkdown>
            </article>
          </div>
        </div>
      </div>
    );
  };

  const renderPdfViewer = (file: any, signedUrl: string) => {
    const isFullscreen = fullscreenPdf === file.id;
    
    if (isFullscreen) {
      return (
        <div className="fixed inset-0 z-50 bg-black">
          {/* PDF Toolbar */}
          <div className={`absolute top-0 left-0 right-0 z-60 bg-black/90 backdrop-blur-sm border-b border-gray-700 transition-all duration-300 ${
            showPdfControls ? 'translate-y-0' : '-translate-y-full'
          }`}>
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setFullscreenPdf(null)}
                  className="text-white hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-white/10"
                  title="Exit fullscreen (Esc)"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="text-white">
                  <h3 className="font-medium">{file.original_filename}</h3>
                  <p className="text-sm text-gray-300">PDF Document</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Zoom Controls */}
                <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
                  <button
                    onClick={() => setPdfZoom(prev => Math.max(50, prev - 10))}
                    className="text-white hover:text-gray-300 transition-colors p-1"
                    title="Zoom out (-)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-white text-sm min-w-12 text-center">{pdfZoom}%</span>
                  <button
                    onClick={() => setPdfZoom(prev => Math.min(200, prev + 10))}
                    className="text-white hover:text-gray-300 transition-colors p-1"
                    title="Zoom in (+)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setPdfZoom(100)}
                    className="text-white hover:text-gray-300 transition-colors p-1 text-xs"
                    title="Reset zoom (0)"
                  >
                    100%
                  </button>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => downloadFile(signedUrl, file.original_filename)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  title="Download PDF"
                >
                  Download
                </button>
                
                {/* Controls Toggle */}
                <button
                  onClick={() => setShowPdfControls(!showPdfControls)}
                  className="text-white hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-white/10"
                  title="Toggle controls"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* PDF Content */}
          <div 
            className="w-full h-full flex items-center justify-center cursor-pointer"
            onClick={() => setShowPdfControls(!showPdfControls)}
          >
            <iframe
              src={`${signedUrl}#zoom=${pdfZoom}`}
              className="w-full h-full border-0"
              title={file.original_filename}
              style={{ 
                transform: `scale(${pdfZoom / 100})`,
                transformOrigin: 'center center'
              }}
            />
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-3 text-white text-xs">
            <div className="space-y-1">
              <div><kbd className="bg-white/20 px-1 rounded">Esc</kbd> Exit fullscreen</div>
              <div><kbd className="bg-white/20 px-1 rounded">+/-</kbd> Zoom in/out</div>
              <div><kbd className="bg-white/20 px-1 rounded">0</kbd> Reset zoom</div>
              <div><kbd className="bg-white/20 px-1 rounded">Click</kbd> Toggle controls</div>
            </div>
          </div>
        </div>
      );
    }

    // Regular PDF viewer - full view
    return (
      <div className="my-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative group">
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 z-10 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 flex space-x-4">
              <button
                onClick={() => setFullscreenPdf(file.id)}
                className="bg-black/70 hover:bg-black/80 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
              >
                📺 View Fullscreen
              </button>
              <button
                onClick={() => downloadFile(signedUrl, file.original_filename)}
                className="bg-blue-600/80 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
              >
                ⬇️ Download
              </button>
            </div>
          </div>
          
          {/* PDF Preview */}
          <iframe
            src={signedUrl}
            className="w-full border-0 pointer-events-none group-hover:pointer-events-auto"
            style={{ height: '90vh', minHeight: '700px' }}
            title={file.original_filename}
          />
        </div>
      </div>
    );
  };

  const renderFileContent = (file: any) => {
    const fileType = file.file_type.toLowerCase();
    const signedUrl = fileUrls[file.id];
    
    switch (fileType) {
      case 'pdf':
        if (!signedUrl) {
          return (
            <div className="my-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-center py-32">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600">Loading document...</p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return renderPdfViewer(file, signedUrl);

      case 'md':
      case 'markdown':
        if (!signedUrl) {
          return (
            <div className="my-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-center py-32">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-4"></div>
                    <p className="text-gray-600">Loading markdown...</p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return renderMarkdownViewer(file, signedUrl);
        
      case 'doc':
      case 'docx':
        return (
          <div className="my-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-16 border border-blue-100 text-center">
              <div className="max-w-md mx-auto">
                <div className="text-8xl mb-8">📝</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Document Resource</h3>
                <p className="text-gray-600 mb-10 leading-relaxed text-lg">This document contains additional resources and materials for this lesson.</p>
                {signedUrl ? (
                  <a
                    href={signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl text-lg"
                  >
                    <span className="mr-3 text-xl">📄</span>
                    Open Document
                  </a>
                ) : (
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                )}
              </div>
            </div>
          </div>
        );
        
      case 'mp3':
      case 'wav':
        return (
          <div className="my-8">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-12 border border-purple-100">
              {signedUrl ? (
                <div className="max-w-4xl mx-auto">
                  <div className="text-center mb-8">
                    <div className="text-6xl mb-4">🎵</div>
                    <h3 className="text-xl font-semibold text-gray-900">Audio Content</h3>
                  </div>
                  <audio controls className="w-full h-20 rounded-2xl shadow-lg">
                    <source src={signedUrl} type={`audio/${fileType}`} />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ) : (
                <div className="flex items-center justify-center py-16">
                  <div className="flex items-center space-x-3 text-purple-600">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="font-medium text-lg">Loading audio...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'mp4':
      case 'webm':
      case 'ogg':
        return (
          <div className="my-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {signedUrl ? (
                <video controls className="w-full" style={{ height: '70vh', minHeight: '500px' }}>
                  <source src={signedUrl} type={`video/${fileType}`} />
                  Your browser does not support the video element.
                </video>
              ) : (
                <div className="flex items-center justify-center py-32">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-4"></div>
                    <p className="text-gray-600">Loading video...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
      case 'webp':
        if (!signedUrl) {
          return (
            <div className="my-8">
              <div className="bg-gray-50 rounded-2xl py-32 border border-gray-100">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-4"></div>
                  <p className="text-gray-600">Loading image...</p>
                </div>
              </div>
            </div>
          );
        }
        return renderImageViewer(file, signedUrl);
        
      default:
        return (
          <div className="my-8">
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-16 border border-gray-100 text-center">
              <div className="max-w-md mx-auto">
                <div className="text-8xl mb-8">📁</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Download Resource</h3>
                <p className="text-gray-600 mb-10 leading-relaxed text-lg">This file contains additional materials for this lesson.</p>
                {signedUrl ? (
                  <a
                    href={signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-10 py-4 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl text-lg"
                  >
                    <span className="mr-3 text-xl">⬇️</span>
                    Download File
                  </a>
                ) : (
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-medical-50 to-tech-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-8">
            <img 
              src="/logo-icon.svg" 
              alt="SC△NMEDX"
              className="w-16 h-16 mx-auto mb-4 animate-pulse"
            />
          </div>
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-medical-500 mb-6"></div>
          <p className="text-lg text-gray-700 font-medium">Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error || !course || !currentLesson) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-8xl mb-8">😞</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Lesson Not Found</h1>
          <p className="text-gray-600 mb-12 text-lg leading-relaxed">{error || 'The lesson you are looking for does not exist.'}</p>
          <Link
            to={courseId ? `/courses/${courseId}` : '/courses'}
            className="inline-flex items-center px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-full transition-all duration-200 hover:scale-105"
          >
            <span className="mr-2">←</span>
            Back to Course
          </Link>
        </div>
      </div>
    );
  }

  const sortedLessons = [...course.lessons].sort((a, b) => a.order - b.order);
  const currentIndex = sortedLessons.findIndex(l => l.id === currentLesson.id);
  const previousLesson = currentIndex > 0 ? sortedLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < sortedLessons.length - 1 ? sortedLessons[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Inject unified lesson styles */}
      <style dangerouslySetInnerHTML={{ __html: unifiedLessonStyles }} />
      {/* Reading Progress Bar */}
      {showProgress && !fullscreenPdf && !fullscreenImage && (
        <div className="fixed top-0 left-0 w-full z-50">
          <div className="h-1 bg-gray-100">
            <div 
              className="h-1 bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 ease-out"
              style={{ width: `${readingProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Sidebar Navigation */}
      {!fullscreenPdf && !fullscreenImage && (
        <>
          {/* Sidebar Overlay */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          
          {/* Sidebar */}
          <div className={`fixed left-0 top-0 h-full w-80 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}>
            <div className="flex flex-col h-full">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg overflow-hidden">
                    <img 
                      src="/logo-icon.svg" 
                      alt="SC△NMEDX"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Course Navigation</h2>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Course Overview */}
              <div className="p-6 border-b border-gray-100">
                <Link 
                  to={`/courses/${courseId}`}
                  className="block hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{course.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{course.description}</p>
                  <div className="flex items-center mt-3 text-xs text-gray-500">
                    <span>{course.instructor.full_name}</span>
                    <span className="mx-2">•</span>
                    <span>{sortedLessons.length} lessons</span>
                  </div>
                </Link>
              </div>

              {/* Progress Overview */}
              {progress && (
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-500">{progress.progress_percentage.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress.progress_percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{progress.completed_lessons} completed</span>
                    <span>{progress.total_lessons} total</span>
                  </div>
                </div>
              )}

              {/* Lessons Navigation */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 px-2">Lessons</h4>
                  <div className="space-y-1">
                    {sortedLessons.map((lesson, index) => {
                      const isCurrentLesson = lesson.id === currentLesson.id;
                      const isCompleted = progress ? progress.completed_lesson_ids.includes(lesson.id) : false;
                      const lessonNumber = lesson.order;
                      
                      return (
                        <Link
                          key={lesson.id}
                          to={`/courses/${courseId}/lessons/${lesson.id}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`block p-3 rounded-lg transition-all duration-200 ${
                            isCurrentLesson 
                              ? 'bg-blue-50 border border-blue-200 shadow-sm' 
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            {/* Lesson Status Icon */}
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              isCompleted 
                                ? 'bg-green-500 text-white' 
                                : isCurrentLesson
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-200 text-gray-600'
                            }`}>
                              {isCompleted ? (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                lessonNumber
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h5 className={`text-sm font-medium truncate ${
                                  isCurrentLesson ? 'text-blue-900' : 'text-gray-900'
                                }`}>
                                  {lesson.title}
                                </h5>
                                {isCurrentLesson && (
                                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                    Current
                                  </span>
                                )}
                              </div>
                              {lesson.description && (
                                <p className={`text-xs line-clamp-2 ${
                                  isCurrentLesson ? 'text-blue-700' : 'text-gray-500'
                                }`}>
                                  {lesson.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Sidebar Footer */}
              <div className="p-4 border-t border-gray-200">
                <Link
                  to={`/courses/${courseId}`}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Course
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Sidebar Toggle */}
      {!fullscreenPdf && !fullscreenImage && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed top-20 left-4 z-30 p-3 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Header */}
      {!fullscreenPdf && !fullscreenImage && (
        <header className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-40 lg:ml-80">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <Link
                  to={`/courses/${courseId}`}
                  className="flex items-center text-gray-500 hover:text-gray-900 transition-colors font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {course.title}
                </Link>
                <div className="hidden sm:flex items-center space-x-3">
                  <span className="bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                    Lesson {currentIndex + 1} of {sortedLessons.length}
                  </span>
                  {isCompleted && (
                    <span className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1 rounded-full">
                      ✓ Completed
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Progress Percentage */}
                {progress && (
                  <span className="hidden md:block text-sm text-gray-500">
                    {progress.progress_percentage.toFixed(0)}% Complete
                  </span>
                )}
                
                {!isCompleted && (
                  <button
                    onClick={handleCompleteLesson}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm"
                  >
                    Mark Complete
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      {!fullscreenPdf && !fullscreenImage && (
        <main className="max-w-6xl mx-auto px-6 py-16 lg:ml-80">
          {/* Article Header */}
          <header className="mb-16 text-center">
            <div className="mb-8">
              <span className="bg-blue-50 text-blue-700 text-sm font-medium px-4 py-2 rounded-full">
                Lesson {currentIndex + 1}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {currentLesson.title}
            </h1>
            {currentLesson.description && (
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                {currentLesson.description}
              </p>
            )}
            <div className="mt-8 pt-8 border-t border-gray-100">
              <div className="flex items-center justify-center text-sm text-gray-500">
                <span>{course.instructor.full_name}</span>
              </div>
            </div>
          </header>

          {/* Lesson Content - Unified Article Experience */}
          <article className="prose prose-lg max-w-none">
            {currentLesson.files && currentLesson.files.length > 0 ? (
              <div className="unified-lesson-content space-y-0">
                {/* Render files in order to create a unified article experience */}
                {currentLesson.files
                  .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                  .map((file: any, index: number) => {
                    const fileType = file.file_type.toLowerCase();
                    const signedUrl = fileUrls[file.id];
                    
                    // For markdown files, render inline without container
                    if ((fileType === 'md' || fileType === 'markdown') && markdownContent[file.id]) {
                      return (
                        <div key={file.id} className="markdown-section">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight]}
                            components={{
                              // Custom styling for different elements
                              h1: ({ node, children, ...props }) => <h1 className="text-4xl font-bold text-gray-900 mb-8 mt-16 first:mt-0 pb-4 border-b border-gray-200" {...props}>{children}</h1>,
                              h2: ({ node, children, ...props }) => <h2 className="text-3xl font-semibold text-gray-900 mb-6 mt-12" {...props}>{children}</h2>,
                              h3: ({ node, children, ...props }) => <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-8" {...props}>{children}</h3>,
                              h4: ({ node, children, ...props }) => <h4 className="text-xl font-semibold text-gray-900 mb-3 mt-6" {...props}>{children}</h4>,
                              h5: ({ node, children, ...props }) => <h5 className="text-lg font-semibold text-gray-900 mb-2 mt-4" {...props}>{children}</h5>,
                              h6: ({ node, children, ...props }) => <h6 className="text-base font-semibold text-gray-900 mb-2 mt-4" {...props}>{children}</h6>,
                              p: ({ node, ...props }) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
                              a: ({ node, children, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline" {...props}>{children}</a>,
                              ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-6 space-y-2" {...props} />,
                              ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-6 space-y-2" {...props} />,
                              li: ({ node, ...props }) => <li className="text-gray-700 leading-relaxed" {...props} />,
                              blockquote: ({ node, ...props }) => (
                                <blockquote className="border-l-4 border-blue-500 pl-6 py-2 my-6 bg-blue-50 rounded-r-lg" {...props} />
                              ),
                              code: ({ node, ...props }: any) => 
                                props.inline ? (
                                  <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono" {...props} />
                                ) : (
                                  <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono" {...props} />
                                ),
                              pre: ({ node, ...props }) => (
                                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-6" {...props} />
                              ),
                              table: ({ node, ...props }) => (
                                <div className="overflow-x-auto mb-6">
                                  <table className="min-w-full border border-gray-200 rounded-lg" {...props} />
                                </div>
                              ),
                              thead: ({ node, ...props }) => <thead className="bg-gray-50" {...props} />,
                              th: ({ node, ...props }) => <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b border-gray-200" {...props} />,
                              td: ({ node, ...props }) => <td className="px-4 py-3 text-gray-700 border-b border-gray-100" {...props} />,
                              hr: ({ node, ...props }) => <hr className="my-8 border-gray-200" {...props} />,
                              img: ({ node, alt, ...props }) => (
                                <img className="max-w-full h-auto rounded-lg shadow-lg mx-auto my-6" alt={alt || 'Image'} {...props} />
                              ),
                            }}
                          >
                            {DOMPurify.sanitize(markdownContent[file.id])}
                          </ReactMarkdown>
                        </div>
                      );
                    }
                    
                    // For images, render inline with the article
                    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(fileType) && signedUrl) {
                      return (
                        <div key={file.id} className="image-section my-8">
                          <div className="relative group">
                            {/* Download button overlay */}
                            <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <button
                                onClick={() => downloadFile(signedUrl, file.original_filename)}
                                className="bg-black/70 hover:bg-black/80 text-white px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                              >
                                ⬇️
                              </button>
                            </div>
                            
                            {/* Fullscreen button overlay */}
                            <div className="absolute top-4 left-4 z-10 opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <button
                                onClick={() => {
                                  setImageZoom(100);
                                  setFullscreenImage(file.id);
                                }}
                                className="bg-black/70 hover:bg-black/80 text-white px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                              >
                                🔍
                              </button>
                            </div>
                            
                            <img
                              src={signedUrl}
                              alt={file.original_filename}
                              className="w-full h-auto rounded-lg shadow-lg cursor-pointer"
                              onClick={() => {
                                setImageZoom(100);
                                setFullscreenImage(file.id);
                              }}
                              style={{ maxHeight: '600px', objectFit: 'contain' }}
                            />
                          </div>
                        </div>
                      );
                    }
                    
                    // For audio files, render inline with the article
                    if (['mp3', 'wav'].includes(fileType) && signedUrl) {
                      return (
                        <div key={file.id} className="audio-section my-12">
                          {/* Modern Audio Player */}
                          <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden group hover:shadow-md transition-all duration-300">
                            {/* Subtle gradient background */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30"></div>
                            
                            {/* Download button */}
                            <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <button
                                onClick={() => downloadFile(signedUrl, file.original_filename)}
                                className="bg-gray-900/80 hover:bg-gray-900 text-white px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                                title="Download audio"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>
                            </div>
                            
                            <div className="relative px-8 py-6">
                              {/* Audio Icon and Label */}
                              <div className="flex items-center space-x-4 mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M8.5 21V3m4 0v18" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                      <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">Audio Content</span>
                                    </div>
                                  </div>
                                  <p className="text-gray-600 text-sm mt-1">Listen while reading to enhance your learning experience</p>
                                </div>
                              </div>
                              
                              {/* Custom Audio Player */}
                              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                <audio 
                                  controls 
                                  className="w-full h-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                                  style={{
                                    filter: 'sepia(0%) saturate(0%) hue-rotate(0deg) brightness(1.1) contrast(1.1)',
                                  }}
                                >
                                  <source src={signedUrl} type={`audio/${fileType}`} />
                                  Your browser does not support the audio element.
                                </audio>
                              </div>
                              
                              {/* Audio metadata */}
                              <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                                <span>Audio resource</span>
                                <span className="font-mono">{fileType.toUpperCase()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // For PDFs and other files, render as before but integrated
                    if (fileType === 'pdf' && signedUrl) {
                      return (
                        <div key={file.id} className="pdf-section my-8">
                          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden relative group">
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 z-10 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 flex space-x-4">
                                <button
                                  onClick={() => setFullscreenPdf(file.id)}
                                  className="bg-black/70 hover:bg-black/80 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                                >
                                  📺 View Fullscreen
                                </button>
                                <button
                                  onClick={() => downloadFile(signedUrl, file.original_filename)}
                                  className="bg-blue-600/80 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                                >
                                  ⬇️ Download
                                </button>
                              </div>
                            </div>
                            
                            {/* PDF Preview */}
                            <iframe
                              src={signedUrl}
                              className="w-full border-0 pointer-events-none group-hover:pointer-events-auto"
                              style={{ height: '600px' }}
                              title={file.original_filename}
                            />
                          </div>
                        </div>
                      );
                    }
                    
                    // For videos, render inline
                    if (['mp4', 'webm', 'ogg'].includes(fileType) && signedUrl) {
                      return (
                        <div key={file.id} className="video-section my-8">
                          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                            <video controls className="w-full" style={{ maxHeight: '500px' }}>
                              <source src={signedUrl} type={`video/${fileType}`} />
                              Your browser does not support the video element.
                            </video>
                          </div>
                        </div>
                      );
                    }
                    
                    // For other file types (DOC, etc.), render as download cards
                    if (['doc', 'docx'].includes(fileType) && signedUrl) {
                      return (
                        <div key={file.id} className="document-section my-8">
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100 text-center">
                            <div className="text-5xl mb-4">📝</div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">Document Resource</h4>
                            <p className="text-gray-600 mb-4 text-sm">{file.original_filename}</p>
                            <a
                              href={signedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                              <span className="mr-2">📄</span>
                              Open Document
                            </a>
                          </div>
                        </div>
                      );
                    }
                    
                    // Loading state for files without signed URLs
                    if (!signedUrl) {
                      return (
                        <div key={file.id} className="loading-section my-8">
                          <div className="bg-gray-50 rounded-xl py-16 border border-gray-100">
                            <div className="text-center">
                              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
                              <p className="text-gray-600">Loading {file.original_filename}...</p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-8xl mb-8">📚</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Coming Soon</h3>
                <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto">
                  The resources for this lesson are being prepared and will be available soon.
                </p>
              </div>
            )}
          </article>

          {/* Lesson Navigation */}
          <nav className="mt-20 pt-12 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {previousLesson ? (
                  <Link
                    to={`/courses/${courseId}/lessons/${previousLesson.id}`}
                    className="group inline-flex items-start space-x-4 text-left hover:bg-gray-50 rounded-2xl p-6 transition-colors"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                      <span className="text-xl">←</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Previous</p>
                      <p className="font-medium text-gray-900 group-hover:text-gray-700">
                        {previousLesson.title}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <div></div>
                )}
              </div>
              
              <div className="flex-1 flex justify-end">
                {nextLesson ? (
                  <Link
                    to={`/courses/${courseId}/lessons/${nextLesson.id}`}
                    className="group inline-flex items-start space-x-4 text-right hover:bg-blue-50 rounded-2xl p-6 transition-colors"
                  >
                    <div>
                      <p className="text-sm text-blue-500 mb-1">Next</p>
                      <p className="font-medium text-gray-900 group-hover:text-blue-700">
                        {nextLesson.title}
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <span className="text-xl text-blue-600">→</span>
                    </div>
                  </Link>
                ) : (
                  <Link
                    to={`/courses/${courseId}`}
                    className="group inline-flex items-start space-x-4 text-right hover:bg-gray-50 rounded-2xl p-6 transition-colors"
                  >
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Finish</p>
                      <p className="font-medium text-gray-900 group-hover:text-gray-700">
                        Back to Course
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                      <span className="text-xl">→</span>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </nav>

          {/* Course Progress */}
          {progress && (
            <div className="mt-16 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Progress</h3>
                <div className="max-w-md mx-auto mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>{progress.completed_lessons} of {progress.total_lessons} lessons</span>
                    <span>{progress.progress_percentage.toFixed(0)}% complete</span>
                  </div>
                  <div className="bg-white rounded-full h-3 shadow-inner">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progress.progress_percentage}%` }}
                    />
                  </div>
                </div>
                <p className="text-gray-600">Keep up the great work!</p>
              </div>
            </div>
          )}
        </main>
      )}

      {/* Fullscreen Viewers */}
      {fullscreenPdf && currentLesson.files?.map((file: any) => 
        file.id === fullscreenPdf && fileUrls[file.id] ? renderFileContent(file) : null
      )}
      {fullscreenImage && currentLesson.files?.map((file: any) => 
        file.id === fullscreenImage && fileUrls[file.id] ? renderFileContent(file) : null
      )}
    </div>
  );
};

export default LessonView;